import moment from "moment";
import { useSelector } from "react-redux";

// One list of what each role can do in Ubil+. Every page and button check reads from it, and
// Settings > People & access describes the roles from it, so changing a role here changes both.
//
// The model: Billing, Accounting and Disconnects each own their job. Admin can change records
// outside those jobs. Everyone can view, and do the simple things below. Super admins can do
// everything. These checks are in the browser only; the API doesn't enforce them yet.
export const CAPABILITIES = {
  // Billing
  runBilling: { roles: ["billing"], label: "Run billing: Billing Wizard, Billing Dashboard and batch resets" },
  bills: { roles: ["billing"], label: "Create, regenerate and delete bills" },
  meterReadings: { roles: ["billing"], label: "Upload meter readings, mark uploads billed, add or edit readings" },
  billingRates: { roles: ["billing"], label: "Rider rates, vendor rates and base charges, common-area charges, rate references and property utility settings" },
  moves: { roles: ["billing"], label: "Moves: move-ins and outs, changing an account's unit, setting a unit's occupant" },
  // Accounting
  payments: { roles: ["accounting"], label: "Add payments, credits, refunds and write-offs, and reverse payments" },
  paymentSetup: { roles: ["accounting"], label: "Payment plans, autopay, winter crisis, bad cards and checks, collection and stop-payment flags" },
  accountingTools: { roles: ["accounting"], label: "Accounting page, Lockbox, Bulk Check and GL codes" },
  // Disconnects
  disconnects: { roles: ["disconnects"], label: "Disconnects: create, process and cancel them, holds, schedules, property groups, contract invoices and the Command Center" },
  // Admin
  editRecords: { roles: ["administration"], label: "Add and edit accounts, properties, units, meters, vendors and property managers" },
  portalUsers: { roles: ["administration"], label: "Manage customer portal users" },
  manageStaff: { roles: ["administration"], label: "People & access and Customer site" },
  deleteWorkOrders: { roles: ["administration"], label: "Delete work orders" },
  // Super admin only
  billingRules: { roles: [], label: "Change billing rules" },
  inviteStaff: { roles: [], label: "Invite people" },
  apiKeys: { roles: [], label: "API Keys (when turned on in Billing rules)" },
  customerType: { roles: [], label: "Change an account's customer type" },
  deleteAccounts: { roles: [], label: "Delete accounts and units" },
};

export const EVERYONE_CAN = "View everything, and add notes, complaints, work orders and run reports.";

export const ROLES = [
  { key: "isSuperUser", name: "Super admin" },
  { key: "administration", name: "Admin" },
  { key: "billing", name: "Billing" },
  { key: "accounting", name: "Accounting" },
  { key: "disconnects", name: "Disconnects" },
].map((role) => ({
  ...role,
  opens:
    role.key === "isSuperUser"
      ? `${Object.values(CAPABILITIES).filter((c) => c.roles.length === 0).map((c) => c.label).join(". ")}. Includes every role below.`
      : `${Object.values(CAPABILITIES).filter((c) => c.roles.includes(role.key)).map((c) => c.label).join(". ")}.`,
}));

// Pages that need a role to open. The sidebar hides these for people without it.
export const ROUTE_CAPABILITIES = [
  { path: "/billingwizard", capability: "runBilling" },
  { path: "/billingdashboard", capability: "runBilling" },
  { path: "/meter-readings-upload", capability: "meterReadings" },
  { path: "/moves", capability: "moves" },
  { path: "/move-ins-outs", capability: "moves" },
  { path: "/accounting", capability: "accountingTools" },
  { path: "/lockbox-upload", capability: "accountingTools" },
  { path: "/bulkcheckentry", capability: "accountingTools" },
  { path: "/disconnect", capability: "disconnects" },
  { path: "/disconnect-management", capability: "disconnects" },
  { path: "/disconnect-command-center", capability: "disconnects" },
  { path: "/disconnect-schedules", capability: "disconnects" },
  { path: "/contract-invoices", capability: "disconnects" },
  { path: "/batch-units", capability: "editRecords" },
  { path: "/api-keys", capability: "apiKeys" },
];

export const capabilityForPath = (path) => {
  const match = ROUTE_CAPABILITIES.find((r) => path === r.path || String(path || "").startsWith(`${r.path}/`));
  return match ? match.capability : null;
};

export const roleName = (key) => ROLES.find((r) => r.key === key)?.name || key;

// Flags come back as 1/0, true/false, "1"/"0" or null (no UserPrivilege row).
export const flag = (value) => value === true || Number(value) === 1;

export const hasRole = (user, key) => flag(user?.isSuperUser) || flag(user?.[key]);

export const can = (user, capability) => {
  if (flag(user?.isSuperUser)) return true;
  const entry = CAPABILITIES[capability];
  if (!entry) return false;
  return entry.roles.some((key) => flag(user?.[key]));
};

// The roles that unlock a capability, for "needs the X role" messages.
export const rolesFor = (capability) => {
  const roles = CAPABILITIES[capability]?.roles || [];
  return roles.length ? roles.map(roleName) : ["Super admin"];
};

export const needsMessage = (capability) => `This needs the ${rolesFor(capability).join(" or ")} role.`;

// Reads the signed-in user from the store: `const can = useCan(); can("payments")`.
export const useCan = () => {
  const user = useSelector((state) => state.user?.details);
  return (capability) => can(user, capability);
};

export const canManagePeople = (user) => can(user, "manageStaff");

// "Who can open…" in People & access: every capability plus the things everyone can do.
export const PAGES = [
  ...Object.entries(CAPABILITIES).map(([key, c]) => ({ label: c.label, capability: key, roles: c.roles })),
  { label: "View everything, notes, complaints, work orders and reports", capability: null, roles: [] },
];

export const canOpen = (user, page) => (page.capability ? can(user, page.capability) : true);

// Logins used by the nightly batch and system jobs. There's no flag for these in the database,
// so they're recognized by mailbox name and kept out of the stale-account review.
export const isServiceAccount = (user) => /^(system|apl-batch|batch)[^@]*@/i.test(user?.email || "");

// A row without a UserPrivilege record comes back with every role column null.
export const hasRoleRecord = (user) => ["administration", "billing", "accounting", "disconnects"].some((k) => user?.[k] !== null && user?.[k] !== undefined);

export const STALE_DAYS = 365;

export const isStale = (user) =>
  flag(user?.isActive) &&
  !isServiceAccount(user) &&
  (!user?.lastLogin || moment().diff(moment(user.lastLogin), "days") > STALE_DAYS);

export const fullName = (user) => `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || "Unnamed";
